import { CheckIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Button } from '@/components/ui/catalyst/button';
import { Text } from '@/components/ui/catalyst/text';
import { TreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';

import { useVoiceAIIntervalUpdate } from './hooks/useVoiceAIIntervalUpdate';
import { useVoiceAIStatus } from './hooks/useVoiceAIStatus';
import { VoiceAIIntervalSelector } from './VoiceAIIntervalSelector';
import { VoiceAIStatusIndicator } from './VoiceAIStatusIndicator';

interface VoiceAIEditableFieldProps {
  request: TreatmentRecordRequestSchema;
  matterId: string;
  providerId: string;
  requestId: string;
  onUpdate?: () => void;
}

export const VoiceAIEditableField: React.FC<VoiceAIEditableFieldProps> = ({
  request,
  matterId,
  providerId,
  requestId,
  onUpdate,
}) => {
  const voiceAI = useVoiceAIStatus(request);
  const {
    isEditing,
    setIsEditing,
    editedInterval,
    setEditedInterval,
    handleUpdate,
    handleCancel,
  } = useVoiceAIIntervalUpdate(request, matterId, providerId, requestId);

  if (!isEditing) {
    return (
      <div className="flex items-center gap-2">
        <VoiceAIStatusIndicator request={request} />
        {voiceAI.canEdit && (
          <Button
            outline
            onClick={() => setIsEditing(true)}
            className="h-6 px-2"
          >
            <PencilIcon className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <VoiceAIIntervalSelector
          value={editedInterval}
          onChange={setEditedInterval}
          status={request.status}
          showHelperText={false}
        />
        <div className="flex items-center gap-1">
          <Button
            onClick={async () => {
              const success = await handleUpdate();
              if (success && onUpdate) onUpdate();
            }}
            className="inline-flex items-center justify-center h-8 px-3 text-sm font-medium rounded-md bg-zinc-900 text-white hover:bg-zinc-800"
          >
            <CheckIcon className="h-3.5 w-3.5 mr-1" />
            Save
          </Button>
          <Button
            outline
            onClick={handleCancel}
            className="inline-flex items-center justify-center h-8 px-3 text-sm font-medium rounded-md border border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50"
          >
            <XMarkIcon className="h-3.5 w-3.5 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
      <Text className="text-xs text-gray-500">
        {request.status === 'pending'
          ? 'Will apply after submission. Default: Every 3 days'
          : 'Changes apply to calls after the next scheduled one'}
      </Text>
    </div>
  );
};
